import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ProductCard from '@/components/base/ProductCard';
import WhatsAppFloat from '@/components/feature/WhatsAppFloat';
import PageHero from '@/components/feature/PageHero';
import SectionHeading from '@/components/base/SectionHeading';
import CTABanner from '@/components/feature/CTABanner';
import FAQAccordion from '@/components/base/FAQAccordion';
import { steelFurniture } from '@/mocks/products';
import { useLazySection } from '@/hooks/useLazySection';
import { useSEO } from '@/hooks/useSEO';

const faqs = [
  { q: 'Quais produtos de aço a Home Office Premium vende em Caruaru?', a: 'Vendemos armários de aço, estantes metálicas, arquivos de aço, lockers corporativos e gaveteiros da marca W3 Indústria em Caruaru PE. Todos com pintura eletrostática e tratamento antiferrugem.' },
  { q: 'Os armários de aço atendem as normas NR18 e NR24?', a: 'Sim. Os mobiliários de aço W3 Indústria atendem as normas NR18 e NR24, sendo ideais para vestieiros, almoxarifados e áreas industriais em Caruaru e região.' },
  { q: 'A Home Office Premium entrega mobiliário de aço em Caruaru e região?', a: 'Sim. Realizamos entrega e montagem em Caruaru e toda a região do Agreste e Sertão de Pernambuco, com equipe própria.' },
  { q: 'Qual a vantagem do mobiliário de aço em relação ao de madeira?', a: 'O mobiliário de aço é mais resistente a impactos, umidade e pragas (cupins). A pintura eletrostática garante durabilidade superior e fácil limpeza. Ideal para almoxarifados, vestieiros e áreas industriais.' },
];

export default function AcoPage() {
  const [gridRef, gridVisible] = useLazySection<HTMLDivElement>();

  useSEO({
    title: 'Mobiliário de Aço Caruaru PE | Armários, Lockers e Estantes W3 Indústria',
    description: 'Mobiliário de aço em Caruaru PE. Armários, estantes, arquivos e lockers W3 Indústria com pintura eletrostática e tratamento antiferrugem. Normas NR18 e NR24. Entrega e montagem em todo o Agreste PE.',
    keywords: 'mobiliário aço Recife, armário aço escritório Petrolina, W3 Indústria Pernambuco, lockers corporativos Garanhuns, estante aço Arcoverde, arquivo aço Sertão PE, armário industrial Agreste, armário aço Nordeste, armário vestiário Recife, gaveteiro aço Petrolina, estante metalica Caruaru, armário ferramenta Pernambuco, locker academia Recife, armário escolar Petrolina',
    path: '/aco',
    ogTitle: 'Mobiliário de Aço Caruaru PE | W3 Indústria – Home Office Premium',
    ogDescription: 'Armários, estantes, arquivos e lockers W3 Indústria em Caruaru PE. Pintura eletrostática, normas NR18 e NR24. Entrega e montagem inclusa.',
  });

  useEffect(() => {
    const schemaId = 'schema-aco';
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) { script = document.createElement('script'); script.id = schemaId; script.type = 'application/ld+json'; document.head.appendChild(script); }
    const siteUrl = import.meta.env.VITE_SITE_URL ?? '';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Mobiliários de Aço – Home Office Premium',
      description: 'Armários, estantes, arquivos e lockers em aço W3 Indústria em Caruaru PE.',
      url: `${siteUrl}/aco`,
    });

    const breadcrumbId = 'schema-aco-breadcrumb';
    let bcScript = document.getElementById(breadcrumbId) as HTMLScriptElement | null;
    if (!bcScript) { bcScript = document.createElement('script'); bcScript.id = breadcrumbId; bcScript.type = 'application/ld+json'; document.head.appendChild(bcScript); }
    bcScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Produtos', item: `${siteUrl}/produtos` },
        { '@type': 'ListItem', position: 3, name: 'Mobiliários de Aço', item: `${siteUrl}/aco` },
      ],
    });

    const faqId = 'schema-aco-faq';
    let faqScript = document.getElementById(faqId) as HTMLScriptElement | null;
    if (!faqScript) { faqScript = document.createElement('script'); faqScript.id = faqId; faqScript.type = 'application/ld+json'; document.head.appendChild(faqScript); }
    faqScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    return () => { script?.remove(); bcScript?.remove(); faqScript?.remove(); };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <PageHero
        eyebrow="W3 Indústria"
        eyebrowIcon="ri-archive-line"
        title={
          <>
            Mobiliário de aço com <span className="text-white/50 font-light">durabilidade industrial.</span>
          </>
        }
        subtitle="Armários, estantes, arquivos e lockers com pintura eletrostática e tratamento antiferrugem. Segurança para seu escritório ou almoxarifado."
        image="https://readdy.ai/api/search-image?query=industrial%20steel%20office%20storage%20cabinets%20metal%20lockers%20clean%20professional%20corporate%20environment%2C%20minimalist%20design%2C%20neutral%20tones%2C%20premium%20product%20photography%2C%20high%20detail&width=1600&height=1000&seq=aco-hero-2026&orientation=landscape"
        alt="Mobiliário de aço W3 Indústria em Caruaru PE"
        breadcrumb={[
          { label: 'Home', to: '/' },
          { label: 'Produtos', to: '/produtos' },
          { label: 'Mobiliários de Aço' },
        ]}
        ctas={[
          { label: 'Solicitar orçamento', to: '/contato' },
          { label: 'Falar no WhatsApp', href: 'https://wa.me/5581982030285?text=Oi%2C%20tudo%20bem%3F%20Vim%20pelo%20site%20e%20gostaria%20de%20mais%20informa%C3%A7%C3%B5es', variant: 'secondary' },
        ]}
      />

      {/* Modelos */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <SectionHeading
              eyebrow="Catálogo"
              title="Modelos disponíveis."
              subtitle={`${steelFurniture.length} produtos para vestiários, almoxarifados e áreas industriais.`}
            />
            <span className="text-[13px] font-medium text-foreground-400 whitespace-nowrap">W3 Indústria</span>
          </div>

          <div ref={gridRef} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-12" data-product-shop>
            {gridVisible && steelFurniture.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-50">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Dúvidas frequentes" title="FAQ — Mobiliário de aço." className="mb-12" />
          <div className="max-w-3xl">
            {faqs.map((f) => (
              <FAQAccordion key={f.q} question={f.q} answer={f.a} />
            ))}
          </div>
        </div>
      </section>

      {/* Complete seu escritório */}
      <section className="bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-20 md:py-28">
          <SectionHeading eyebrow="Continue explorando" title="Explore outros produtos corporativos." className="mb-10" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: 'Cadeiras ergonômicas', sub: 'Frisokar · Plaxmetal', to: '/cadeiras', icon: 'ri-armchair-line' },
              { label: 'Mesas corporativas', sub: 'Marzo Vitorino', to: '/mesas', icon: 'ri-layout-grid-line' },
              { label: 'Divisórias modulares', sub: 'Sem obra', to: '/divisorias', icon: 'ri-layout-masonry-line' },
            ].map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="group flex items-center gap-4 bg-background-50 rounded-xl border border-background-200/60 p-5 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-white text-foreground-500 group-hover:text-primary-600 transition-colors flex-shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </span>
                <span>
                  <span className="block text-foreground-900 font-medium text-[14px] group-hover:text-primary-600 transition-colors">{item.label}</span>
                  <span className="block text-foreground-400 text-[12px] font-light">{item.sub}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Precisa equipar almoxarifado ou vestiário?"
        subtitle="Atendemos as normas NR18 e NR24, com entrega e montagem em toda a região."
        primaryLabel="Falar com um especialista"
        primaryHref="https://wa.me/5581982030285?text=Ol%C3%A1%20Ednilson!%20Vi%20o%20site%20da%20HomeOffice%20Premium.%20Gostaria%20de%20conversar%20sobre%20mobili%C3%A1rio%20de%20a%C3%A7o."
      />

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}